import theme from 'src/theme';

const TABLE_HEIGHT = 560;

export const tableSx = {
    root: {
        width: '100%',
        height: TABLE_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        // The fixed height keeps the pager in one place while you switch tabs, which is worth having
        // on a desktop and unaffordable below it: 560px of chrome on a phone in landscape is taller
        // than the screen. Let the rows decide the height there, capped against the viewport.
        [theme.breakpoints.down('md')]: {
            height: 'auto',
            maxHeight: `min(${TABLE_HEIGHT}px, 80dvh)`,
        },
    },
    headerRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1.2fr 90px 72px 90px',
        gap: 2,
        px: 2,
        pb: 1.25,
        borderBottom: `1px solid ${theme.palette.neutral[100]}`,
        alignItems: 'center',
        flexShrink: 0,
        // Below md a row is no longer a grid but a name, its figure and a bar under them, so there
        // are no columns left to head: the labels would be a column of headings above an unrelated
        // block. From md up this is the five-column grid it has always been.
        [theme.breakpoints.down('md')]: {
            display: 'none',
        },
    },
    headerCell: {
        fontSize: '0.68rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: theme.palette.neutral[500],
    },
    headerCellRight: {
        fontSize: '0.68rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: theme.palette.neutral[500],
        textAlign: 'right',
    },
    body: {
        flex: 1,
        overflowY: 'auto' as const,
        minHeight: 0,
        '&::-webkit-scrollbar': {width: 6},
        '&::-webkit-scrollbar-thumb': {
            bgcolor: 'rgba(0,0,0,0.15)',
            borderRadius: 3,
        },
    },
    row: {
        display: 'grid',
        gridTemplateColumns: '1fr 1.2fr 90px 72px 90px',
        gap: 2,
        px: 2,
        py: 1.5,
        alignItems: 'center',
        borderBottom: `1px solid ${theme.palette.neutral[50]}`,
        transition: 'background-color 0.15s ease',
        '&:hover': {bgcolor: theme.palette.neutral[50]},
        '&:last-child': {borderBottom: 'none'},
        // Same raw query replaced by the theme scale, so md means md here and everywhere else.
        [theme.breakpoints.down('md')]: {
            gridTemplateColumns: '1fr 1fr 72px',
        },
        // A phone cannot hold a name, a bar and two numbers on one line without squeezing all four
        // past reading. One record per column instead, its fields stacked.
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: '1fr',
            gap: 0.75,
            py: 2,
        },
    },
    name: {
        fontSize: '0.875rem',
        fontWeight: 500,
        color: theme.palette.neutral[800],
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    nameSubtitle: {
        fontSize: '0.72rem',
        color: theme.palette.neutral[500],
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    barCell: {
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        minWidth: 0,
    },
    barTrack: {
        flex: 1,
        height: 6,
        bgcolor: theme.palette.neutral[100],
        borderRadius: '3px',
        overflow: 'hidden',
        minWidth: 60,
    },
    barFill: {
        height: '100%',
        borderRadius: '3px',
        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
        transition: 'width 0.4s ease',
    },
    barValue: {
        fontSize: '0.8rem',
        fontWeight: 600,
        color: theme.palette.neutral[800],
        minWidth: 60,
        textAlign: 'right' as const,
        fontVariantNumeric: 'tabular-nums',
    },
    metricRight: {
        fontSize: '0.82rem',
        fontWeight: 500,
        color: theme.palette.neutral[700],
        textAlign: 'right' as const,
        fontVariantNumeric: 'tabular-nums',
        // Right alignment lines figures up against the column beside them. Under sm there is no
        // column beside them, so it only strands the number at the far edge of the stacked record.
        [theme.breakpoints.down('sm')]: {textAlign: 'left'},
    },
    metricHideMobile: {
        fontSize: '0.82rem',
        fontWeight: 500,
        color: theme.palette.neutral[700],
        textAlign: 'right' as const,
        fontVariantNumeric: 'tabular-nums',
        [theme.breakpoints.down('md')]: {display: 'none'},
    },
    bodyEmpty: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.neutral[500],
        fontSize: '0.875rem',
    },
    bodyLoading: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    footer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        pt: 1.5,
        px: 1,
        borderTop: `1px solid ${theme.palette.neutral[100]}`,
        flexShrink: 0,
        flexWrap: 'nowrap',
        // Below md the two groups take the second line rather than the page's horizontal scrollbar.
        [theme.breakpoints.down('md')]: {
            flexWrap: 'wrap',
            rowGap: 1,
        },
    },
    footerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexShrink: 0,
        flexWrap: 'nowrap',
        [theme.breakpoints.down('md')]: {flexWrap: 'wrap'},
    },
    footerRight: {
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        flexShrink: 0,
        flexWrap: 'nowrap',
        [theme.breakpoints.down('md')]: {flexWrap: 'wrap'},
    },
    footerLabel: {
        fontSize: '0.75rem',
        color: theme.palette.neutral[500],
        whiteSpace: 'nowrap',
    },
    footerLabelHideMobile: {
        fontSize: '0.75rem',
        color: theme.palette.neutral[500],
        whiteSpace: 'nowrap',
        // Was a raw 720px, a number belonging to no tier. The strip wraps from md down now, so the
        // labels only have to go where there is genuinely no room for them - the phone tier.
        [theme.breakpoints.down('sm')]: {display: 'none'},
    },
    sizeSelect: {
        minWidth: 72,
        '& .MuiOutlinedInput-root': {
            height: 32,
            fontSize: '0.82rem',
            borderRadius: '8px',
            bgcolor: theme.palette.neutral[50],
            '& fieldset': {borderColor: 'transparent'},
            '&:hover fieldset': {borderColor: theme.palette.neutral[200]},
            '&.Mui-focused fieldset': {borderColor: theme.palette.primary.main},
        },
    },
    pageBtn: {
        minWidth: 32,
        height: 32,
        borderRadius: '8px',
        fontSize: '0.82rem',
        textTransform: 'none',
        color: theme.palette.neutral[700],
        border: `1px solid ${theme.palette.neutral[200]}`,
        '&:hover': {
            borderColor: theme.palette.primary.light,
            bgcolor: 'rgba(0, 127, 220, 0.03)',
        },
        '&.Mui-disabled': {opacity: 0.4},
    },
    pageInfo: {
        fontSize: '0.78rem',
        color: theme.palette.neutral[500],
        mx: 1,
        fontVariantNumeric: 'tabular-nums',
    },
    // Below md the record is a line and a bar, as drawn: who it is and how much on top, the share of
    // the largest value beneath them. Four numbers abreast at 390px squeezed all four past reading.
    phoneRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        px: '14px',
        py: '12px',
        borderBottom: `1px solid ${theme.palette.neutral[50]}`,
        '&:last-of-type': {borderBottom: 'none'},
    },
    phoneRowTop: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '10px',
    },
    phoneName: {
        fontSize: '14px',
        fontWeight: 500,
        color: theme.palette.text.primary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    phoneSubtitle: {
        fontSize: '12px',
        color: theme.palette.neutral[500],
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    phoneValue: {
        flexShrink: 0,
        fontSize: '14px',
        fontWeight: 600,
        color: theme.palette.text.primary,
        textAlign: 'right' as const,
        fontVariantNumeric: 'tabular-nums',
    },
    phoneBarTrack: {
        height: 6,
        borderRadius: '3px',
        bgcolor: theme.palette.neutral[100],
        overflow: 'hidden',
    },
    phoneBarFill: {
        height: '100%',
        borderRadius: '3px',
        transition: 'width 0.4s ease',
    },
};